import { Download, Printer } from 'lucide-react';
import { SLIP_SIZE_LABELS, type SlipSize, slipSizeSchema } from '#shared/types';
import { Checkbox } from '#client/components/ui/Checkbox';
import { Modal } from '#client/components/ui/Modal';
import { Segmented } from '#client/components/ui/Segmented';
import { useCan } from '#client/hooks/useCan';
import { printUrls } from '#client/lib/endpoints';
import { readSlipSize, writeSlipSize } from './slip-size';
import { useState } from 'react';

const SIZE_OPTIONS = slipSizeSchema.options.map((value) => ({
  value,
  label: SLIP_SIZE_LABELS[value],
}));

interface SlipDialogProps {
  open: boolean;
  onClose: () => void;
  patientId: number;
  patientName: string;
}

/**
 * Print the registration slip, on whichever paper is in the tray.
 *
 * The options live here and nowhere else, so the patient screen, the
 * just-registered prompt and anything added later cannot offer different ones.
 *
 * Print and Download are real `<a>` elements rather than fetches: the endpoint
 * returns bytes, and an anchor keeps middle-click, "open in new tab" and
 * "save as" working. Same reasoning as the rate list's print button.
 */
export function SlipDialog({ open, onClose, patientId, patientName }: SlipDialogProps) {
  const can = useCan();

  // The tray and the receipt printer do not change per patient, so the last
  // choice is remembered per browser. Validated on the way out of storage
  // rather than trusted — a hand-edited key must not put `?size=junk` on a URL.
  const [size, setSize] = useState<SlipSize>(readSlipSize);

  // A technician opening the slip wants the copy that travels with the sample;
  // the front desk wants the one the patient is handed. Presentation only — the
  // endpoint is open to every signed-in role by design, because the bench copy
  // has to be printable by the bench.
  const [charges, setCharges] = useState(() => can('billing.view'));

  const handleChoose = (next: SlipSize) => {
    setSize(next);
    writeSlipSize(next);
  };

  const options = { size, charges };

  return (
    <Modal open={open} title={`Print slip — ${patientName}`} onClose={onClose}>
      <div className="c-form-stack">
        <Segmented label="Paper size" options={SIZE_OPTIONS} value={size} onChange={handleChoose} />

        <Checkbox
          label="Show charges"
          checked={charges}
          onChange={(event) => setCharges(event.target.checked)}
        />
        <p className="c-field__help">
          {charges
            ? 'The copy the patient is handed.'
            : 'The bench copy — no rates or totals, and it travels with the sample.'}
        </p>

        <div className="c-modal__actions">
          <a
            className="c-button c-button--primary c-button--md"
            href={printUrls.slip(patientId, options)}
            target="_blank"
            rel="noopener"
          >
            <Printer aria-hidden />
            Open and print
          </a>
          <a
            className="c-button c-button--secondary c-button--md"
            href={printUrls.slip(patientId, { ...options, download: true })}
          >
            <Download aria-hidden />
            Download
          </a>
        </div>
      </div>
    </Modal>
  );
}
